import type { ReactNode } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'

import { useFormikContext } from 'formik'

import { ContractDtoBidBasisEnum } from '@b-stock/contract-api-client'
import { useDeliveryMethodModal } from '@b-stock/fe-core/bstock-next/components'
import { getNumberOfUnitsInLot } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedAuctionListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'
import type { ActiveServiceDto } from '@b-stock/subscription-api-client'

import useAutoQuoteGeneration from '@/buyer-portal/components/features/listing/hooks/useAutoQuoteGeneration'
import { useCalculatedAdditionalCharges } from '@/buyer-portal/components/features/listing/hooks/useCalculatedAdditionalCharges'
import { useFirstDibsPurchase } from '@/buyer-portal/components/features/listing/hooks/useFirstDibsPurchase'
import { useDisplayPhase } from '@/buyer-portal/helpers/auctions/buyingFormat'
import getCurrentPrice from '@/buyer-portal/helpers/auctions/getCurrentPrice'
import getIsWithinBuyNowWindow from '@/buyer-portal/helpers/auctions/getIsWithinBuyNowWindow'
import { getEditDeliveryMethodRedirectUri } from '@/buyer-portal/helpers/shipping'
import useAccountId from '@/buyer-portal/helpers/useAccountId'

import { type AuctionDetailState } from '../useAuctionDetailState'
import { useBidConfirmationModal } from './BidConfirmationModal/useBidConfirmationModal'
import {
  type BidFormActions,
  BidFormContext,
  type BidFormContextState,
  type BidFormDetails,
  type BuyNowActions,
} from './BidFormContext'
import type { BidFormFormikValues } from './BidFormFormikProvider'
import { useBuyNowConfirmationModal } from './BuyNowConfirmationModal/useBuyNowConfirmationModal'
import { ErrorTooltip, useErrorTooltip } from './ErrorTooltip'
import { hasAuctionEnded } from './hasAuctionEnded'

export type InternalBidFormProviderProps = {
  auctionDetailState: AuctionDetailState
  bidLocation: 'pdp' | 'list'
  children: ReactNode
  currency: string
  doNotify?: boolean
  // For portaled submit outside form in DOM (e.g. inline bid overlay)
  formId?: string
  initialBidAmount?: BidFormFormikValues['amount']
  initialBidType?: BidFormFormikValues['bidType']
  /** GLOB-4832: whether Freeze Sort is active on the originating List View —
   * see useTrackBidConfirm. Only supplied by the List View caller
   * (AuctionActionCellPostload); omitted everywhere else (e.g. LDP). */
  isSortFrozen?: boolean
  listing: OrchestratedAuctionListingDetails
  /** GLOB-4832/BP-2422: see useTrackBidConfirm. Only supplied alongside isSortFrozen. */
  recordBidPlacedWhileFrozen?: () => void
  sellerSubscription?: ActiveServiceDto | null

  selectedQuote?: OrchestratedQuote | null
}

export const InternalBidFormProvider = ({
  auctionDetailState: { auctionState, bidState },
  bidLocation,
  children,
  currency,
  doNotify,
  isSortFrozen,
  listing,
  recordBidPlacedWhileFrozen,
  selectedQuote,
  sellerSubscription,
}: InternalBidFormProviderProps) => {
  const { auction, contract, lot, saleMetrics } = listing
  const bidBasis = contract?.bidBasis ?? ContractDtoBidBasisEnum.Absolute
  const bidUnit = contract?.bidUnit
  const viewCount = saleMetrics?.viewCount

  const accountId = useAccountId()
  const formikContext = useFormikContext<BidFormFormikValues>()
  const bidAmount = formikContext.values.amount || 0

  const [isPaused, setIsPaused] = useState(false)

  const { BidConfirmationModal } = useBidConfirmationModal()

  const isWithinBuyNowWindow = getIsWithinBuyNowWindow(auction)
  // Prices the Buy Now variant of the bid card, which also covers a Buy Now
  // Only listing after it ends and a listing sold via Buy Now.
  const isBinoDisplay = useDisplayPhase(listing) === 'BINO'

  const { earlyPrice: fdEarlyPrice } = useFirstDibsPurchase(listing)

  const currentBidDollars = useMemo(() => {
    const { bid, nextMinBidAmount, winningBidAmount } = auction

    const currentBid = isPaused
      ? (bid?.bidAmount ?? 0)
      : bid?.canceled
        ? nextMinBidAmount
        : Math.max(bid?.bidAmount ?? 0, nextMinBidAmount ?? 0, bidAmount)

    // Mirror BidActionCard: fdEarlyPrice forces the Buy Now variant regardless
    // of the raw purchasing phase (e.g. Scheduled auction with no open BN window).
    if (fdEarlyPrice !== undefined || isBinoDisplay) {
      return fdEarlyPrice ?? auction.buyNow?.price ?? 0
    }
    if (bidState === BidStatus.WON && winningBidAmount) {
      return winningBidAmount
    }
    return currentBid ?? 0
  }, [auction, bidAmount, bidState, fdEarlyPrice, isBinoDisplay, isPaused])

  const frozenDetails = useRef<BidFormContextState['details'] | undefined>(
    undefined
  )

  const additionalCharges = useCalculatedAdditionalCharges({
    buyersPremium: sellerSubscription?.buyersPremium ?? 0,
    currentBidDollars,
    selectedQuote,
  })

  const details = useMemo(() => {
    // Note: The frozenDetails.current !== undefined will never be false if isPaused
    // is true, since we always set it immediately after this memo and isPaused always
    // starts as false, so frozenDetails.current will be defined if isPaused is true
    // oxlint-disable-next-line react/refs
    if (isPaused && frozenDetails.current !== undefined) {
      // we delay all the data for 3000ms for some bs transitioning state lol
      return {
        // oxlint-disable-next-line react/refs
        ...frozenDetails.current,
        userBidAmount: auction.bid?.bidAmount ?? 0,
        isPaused,
      } satisfies BidFormDetails
    } else {
      const totalUnits = getNumberOfUnitsInLot(lot) ?? 0

      const currentPrice = getCurrentPrice(auction)

      return {
        tagStatus: bidState,
        bidCount: auction.numberOfBids,
        viewCount,
        bidDeadline:
          (isWithinBuyNowWindow
            ? auction.buyNow?.endTime
            : // oxlint-disable-next-line react/purity
              auction.actualEndTime) || new Date().toISOString(),
        isEnded: hasAuctionEnded(auctionState),
        auctionState,
        units: totalUnits,
        msrpPrice: lot.retailValue,
        currency,
        bidBasis,
        bidUnit,
        bidPrecision: auction.bidPrecision,
        currentPrice,
        userBidAmount: auction.bid?.canceled
          ? 0
          : (auction.bid?.bidAmount ?? 0),
        additionalCharges,
        isPaused,
        isWithinBuyNowWindow,
      } satisfies BidFormDetails
    }
  }, [
    auction,
    isPaused,
    auctionState,
    lot,
    additionalCharges,
    bidState,
    currency,
    viewCount,
    bidBasis,
    bidUnit,
    isWithinBuyNowWindow,
  ])

  frozenDetails.current = details // oxlint-disable-line react/refs

  const {
    buyNowConfirmationModal,
    onBuyNowConfirm,
    openModal: quickBuy,
  } = useBuyNowConfirmationModal({
    currency,
    listing,
    selectedQuote,
    sellerSubscription,
  })

  const {
    handleNewQuoteSelection,
    deliveryMethodChangePending,
    editDeliveryMethod: openDeliveryMethodModal,
  } = useDeliveryMethodModal()

  // BidFormContext exposes a zero-arg editDeliveryMethod (unlike the modal
  // components, which each call useDeliveryMethodModal directly and can pass
  // their own tracking callbacks) — so the LDP-return redirectURI has to be
  // supplied here, once, for every context consumer (BuyNowActions,
  // BidActions, BidActionsV2, Payment's "select a way to receive goods" link).
  const editDeliveryMethod = useCallback(
    () =>
      openDeliveryMethodModal({
        redirectURI: getEditDeliveryMethodRedirectUri(),
      }),
    [openDeliveryMethodModal]
  )

  const autoQuoteGeneration = useAutoQuoteGeneration(listing)

  const shippingQuotePending =
    deliveryMethodChangePending || autoQuoteGeneration.isLoading

  const bidActions: BidFormActions = {
    handleNewQuoteSelection,
  }

  const buyNowActions: BuyNowActions = {
    onBuyNowConfirm,
    quickBuy,
  }

  const {
    errorTooltip,
    errorTooltipMessage,
    setErrorTooltip,
    setErrorTooltipMessage,
  } = useErrorTooltip()

  const bidFormContextState: BidFormContextState = {
    autoQuoteGeneration,
    bidActions,
    buyNowActions,
    deliveryMethodChangePending,
    details,
    editDeliveryMethod,
    errorTooltip,
    errorTooltipMessage,
    selectedQuote,
    setErrorTooltip,
    setErrorTooltipMessage,
    shippingQuotePending,
  }

  return (
    <BidFormContext.Provider value={bidFormContextState}>
      {children}

      {errorTooltip && errorTooltipMessage && (
        <ErrorTooltip {...errorTooltip} isOpen>
          {errorTooltipMessage}
        </ErrorTooltip>
      )}

      <BidConfirmationModal
        bidLocation={bidLocation}
        buyerAccountId={accountId ?? ''}
        buyersPremium={sellerSubscription?.buyersPremium ?? 0}
        doNotify={doNotify}
        isSortFrozen={isSortFrozen}
        listing={listing}
        recordBidPlacedWhileFrozen={recordBidPlacedWhileFrozen}
        selectedQuote={selectedQuote}
        setIsPaused={setIsPaused}
      />

      {buyNowConfirmationModal}
    </BidFormContext.Provider>
  )
}
